import {
  IS_MULTI_ZONE, ONE_ZONE_REASON, ZONES, consoleZoneLabel, orderForPreselection,
  useWorkingZoneCode, zoneCountryCode, PLATFORM_WIDE_CODE, PLATFORM_WIDE_NAME,
} from '../lib/zones';
import './zone-selector.css';

/**
 * ADR-122 Patterns 1, 4 and 5 — the three pieces every fanned-out list needs.
 *
 * `CustomersPage` and `AlertsPage`, the reference implementation this ADR extends, each built
 * these inline. That was right for two pages; ADR-122 brings the count to seven, and seven
 * hand-rolled copies of "warn when a zone did not answer" is how one of them ends up not
 * warning at all. So the pieces live here once:
 *
 *   - `ZoneCell`     — the Zone column's cell, for a row tagged by the fan-out.
 *   - `ZoneFilter`   — the "All zones" narrowing control.
 *   - `ZoneFanoutWarning` — ⭐⭐ THE ONE THAT MATTERS. A zone that did not respond contributes
 *     no rows, so a short list looks exactly like a complete one and an operator searching for
 *     a record is told, in effect, that it does not exist. `lib/zones.ts` hands the caller the
 *     failures rather than absorbing them precisely so this can be rendered; a page that
 *     fetches `failedZones` and does not display it has re-created the defect at the last step.
 *
 * ⚠ ADR-123 Decision 2 — NONE OF THESE EVER RENDERS NOTHING. They used to disappear in a
 * single-zone console so pre-multi-zone builds looked unchanged; that turned a deploy missing the
 * US address into a console that was AU-only and looked normal, and left UAT unable to tell an
 * absent control from a hidden one. They now always render; in a one-zone bundle the controls are
 * disabled with the reason on screen ("only one zone configured"), and the navigation bar says
 * "1 zone configured — expected 2".
 */

/** A row tagged with its zone by the fan-out. `zone` is optional because a row fetched by a
 *  single-zone path, or one predating zone tagging, legitimately has none. */
export interface ZoneTagged {
  zone?: string;
}

/**
 * The Zone column's cell — and the row label of every per-zone legend table: the zone's COUNTRY CODE (`AU`, `US`),
 * or `All` for a platform-wide row, with the full name in a hover tooltip and as the accessible name (Jeff,
 * 31 October 2026; `admin-portal-ui-standards.md` §6 row 2). It replaced "ap-southeast-2 · Australia" in every
 * cell — "a massive waste of space" in a table. An untagged row shows an em dash, never a guessed zone.
 *
 * ⚠ `role="img"` IS WHAT MAKES THE FULL NAME THE ACCESSIBLE NAME. A plain span's `aria-label` is ignored by screen
 * readers, and a visually-hidden copy of the full name would put the region code back in the cell's text — which the
 * table test (`zone-cell-code.test.tsx`) forbids.
 */
export function ZoneCell({ zone, platformWide = false }: { zone?: string; platformWide?: boolean }) {
  if (platformWide) {
    return (
      <span className="zone-code zone-code-all" role="img" title={PLATFORM_WIDE_NAME} aria-label={PLATFORM_WIDE_NAME}>
        {PLATFORM_WIDE_CODE}
      </span>
    );
  }
  if (!zone) return <>—</>;
  const full = consoleZoneLabel(zone);
  return (
    <span className="zone-code" role="img" title={full} aria-label={full} data-zone-code={zone}>
      {zoneCountryCode(zone)}
    </span>
  );
}

/**
 * The shared "All zones" filter.
 *
 * `value` is a zone code or the literal `'all'`. Pair it with `matchesZone` below rather than
 * re-implementing the comparison per page — the comparison is one line, and one line copied
 * seven times is seven chances to invert it.
 */
export function ZoneFilter({
  value,
  onChange,
  className = 'portal-users-filter-label',
}: {
  value: string;
  onChange: (next: string) => void;
  /** The page's own label class, so this control matches its siblings rather than importing a look. */
  className?: string;
}) {
  // ADR-123 Decision 3 — the filter still DEFAULTS to "All zones"; the working zone (the preferred
  // zone until the operator switches — ADR-124 A2) only decides which zone is listed first.
  // ⚠ NARROWING IS NOT A ZONE SWITCH (ADR-124 A2): it changes what is SHOWN, not what is TARGETED,
  // so it never opens the switch modal. The navigation-bar indicator already names the narrowed zone.
  const ordered = orderForPreselection(ZONES, useWorkingZoneCode());
  return (
    <label className={className}>
      Zone
      <select
        className="portal-select"
        value={value}
        disabled={!IS_MULTI_ZONE}
        title={IS_MULTI_ZONE ? undefined : ONE_ZONE_REASON}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="all">All zones</option>
        {ordered.map((z) => (
          <option key={z.code} value={z.code}>{z.label}</option>
        ))}
      </select>
      {!IS_MULTI_ZONE && <span className="zone-disabled-reason">{ONE_ZONE_REASON}</span>}
    </label>
  );
}

/** Does this row survive the current zone filter? `'all'` keeps everything, including untagged rows. */
export const matchesZone = (row: ZoneTagged, filter: string): boolean =>
  filter === 'all' || row.zone === filter;

/**
 * The partial-read warning. Renders nothing when every zone answered.
 *
 * ⚠ `noun` COMPLETES A SENTENCE ABOUT WHAT IS MISSING, e.g. "leads" → "…leads in that zone are
 * missing from this list." Say what the operator cannot see, not that an error occurred: "one
 * zone failed" is a fact about the system, while "US leads are missing from this list" is the
 * fact the operator needs in order not to act on a list they think is complete.
 */
export function ZoneFanoutWarning({
  failedZones,
  noun,
  onRetry,
}: {
  failedZones: string[];
  noun: string;
  onRetry?: () => void;
}) {
  if (!failedZones.length) return null;
  const plural = failedZones.length !== 1;
  return (
    <div className="portal-error" role="alert">
      {failedZones.join(' and ')} did not respond — {noun} in {plural ? 'those zones are' : 'that zone are'}{' '}
      missing from this list.{' '}
      {onRetry && (
        <button type="button" className="portal-link-button" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}

/**
 * ADR-122 Pattern 2's negative case — a detail page reached with NO `?zone=`, whose
 * single-zone lookup then 404s.
 *
 * ⭐⭐ THE DEFECT THIS EXISTS TO PREVENT IS A TRUE SENTENCE THAT MISLEADS. Pattern 2 is
 * deliberately ONE call, not a fan-out: a record lives in exactly one zone, so asking every
 * zone returns one hit and N−1 404s. The cost of that correctness is a link with no zone on
 * it — an old bookmark, a URL pasted into chat, anything minted before zones existed — which
 * is answered by the home zone alone. If that zone does not hold the record, a bare "not
 * found" is *accurate about the request* and *wrong about the world*: the record exists, in
 * the jurisdiction nobody asked.
 *
 * So the page says which zone it asked, and offers the others as one click each. The operator
 * is never left believing a record was deleted when it was merely somewhere else.
 *
 * ⚠ ADR-123 Decision 2 — in a one-zone bundle it does NOT go quiet. There is then no other zone
 * to offer, but "not found" still may not mean "does not exist": the console may simply have
 * been deployed without the other zone's address. It says so, instead of vanishing.
 */
export function ZoneAmbiguousNotFound({
  askedZone,
  noun,
  onTryZone,
}: {
  /** The zone code that was actually queried — i.e. the home zone. */
  askedZone: string;
  /** What was not found, e.g. "This partner application". */
  noun: string;
  /** Re-run the lookup against another zone. */
  onTryZone: (code: string) => void;
}) {
  const others = ZONES.filter((z) => z.code !== askedZone);
  if (!others.length) {
    return (
      <div className="portal-error" role="alert">
        {noun} was not found in <strong>{consoleZoneLabel(askedZone)}</strong> — the only zone this
        console can reach ({ONE_ZONE_REASON}). If it belongs to another zone, it will not show here.
      </div>
    );
  }
  return (
    <div className="portal-error" role="alert">
      {noun} was not found in <strong>{consoleZoneLabel(askedZone)}</strong>, and this link does not say
      which zone it belongs to — so it may exist in another one rather than not exist at all.{' '}
      {others.map((z) => (
        <button
          key={z.code}
          type="button"
          className="portal-link-button"
          onClick={() => onTryZone(z.code)}
        >
          Look in {z.label}
        </button>
      ))}
    </div>
  );
}
